import pegasus from '../../assets/scripts/global/01-pegasus';

/**
 * allModals
 *
 * Component code for creating all modals on the page
 *
 * 	Usage:
 * 	Add link with the attr 'data-pegasus-modal'.
 * 	Any values passed to the attr will be added as classes on the modal so it can be styled accordingly.
 * 	To include modal navigation, pass the class 'Modal--with-nav' to the data attr. This class will be picked up and the nav will be built.
 *
 * 	Example:
 *  <a href="#" data-pegasus-modal="Modal--team Modal--with-nav"></a>
 *
 * @since  2.0.0
 */
window.allModals = () => {
	return {
		init() {
			const _this = this;
			const modalSkeleton = pegasus.stringToHTML(document.getElementById('Modal__Skeleton').innerHTML);
			// Filter out links that are already initialized to allow safe re-initialization
			const allLinks = document.querySelectorAll('[data-pegasus-modal]');
			const uninitializedLinks = Array.from(allLinks).filter((link) => !link.hasAttribute('data-pegasus-modal-id'));
			const modalLinks = _this.createModalLinks(uninitializedLinks);

			modalLinks.forEach((link, index) => {
				const modal = modalSkeleton.cloneNode(true);

				let cssClass = 'Modal',
					modalNav;

				//check for classes to add
				if (link.dataset && link.dataset.pegasusModal && link.dataset.pegasusModal.length) {
					cssClass += ' ' + link.dataset.pegasusModal;

					//if nav specified
					if (link.dataset.pegasusModal.includes('Modal--with-nav')) {
						modalNav = _this.buildModalNav(index, modalLinks);

						if (modalNav) {
							modal.querySelector('.Modal__navigation').append(modalNav);
						}
					}
				}

				//setup classes and id
				modal.className = cssClass;
				modal.id = link.dataset.pegasusModalId;

				//append modal template to page if it does nt already exist
				if (link.dataset && !document.getElementById(link.dataset.pegasusModalId)) {
					document.body.append(modal);
				}

				// Pre-fetch ≤4 modals when the browser is idle so the request
				// doesn't appear in the critical chain.
				const shouldPrefetch = modalLinks.length <= 4 && !link.dataset.pegasusModal?.includes('Modal--video');

				if (shouldPrefetch) {
					const prefetch = () => _this.loadModalContent(link.href, link.dataset.pegasusModalId);
					if ('requestIdleCallback' in window) {
						requestIdleCallback(prefetch, { timeout: 3000 });
					} else {
						setTimeout(prefetch, 2000);
					}
				}
			});

			// ── Deferred handler state ──────────────────────────────
			// Tracks per-modal state so dynamically imported content
			// handlers (e.g. HubSpot, CF7) can defer their heavy work
			// until the modal is actually opened — even when content
			// is pre-fetched.
			this._modalState = {};

			window.addEventListener('modalopen', (evt) => {
				const id = evt.detail?.modalID;
				if (!id) return;

				const state = (this._modalState[id] = this._modalState[id] || {
					modalBody: null,
					handlerImports: [],
					opened: false,
				});
				state.opened = true;

				// Content already fetched — fire deferred handlers now.
				if (state.modalBody) {
					this._runModalOpenHandlers(id);
				}
			});

			window.modal = this;
		},

		doesLinkAlreadyExist(link, linksArray) {
			return linksArray.find((element) => element.href === link.href);
		},

		createModalLinks(links) {
			const _this = this;
			const modalLinks = [];

			if (links.length) {
				links.forEach((link, index) => {
					const container = link;

					if (!link || link.tagName.toLowerCase() !== 'a') {
						const innerAnchor = container.querySelector('a[href]');
						if (!innerAnchor) return; // No actionable link

						// Ensure modal data attributes exist on the actionable anchor
						if (!innerAnchor.dataset.pegasusModal && container.dataset.pegasusModal) {
							innerAnchor.setAttribute('data-pegasus-modal', container.dataset.pegasusModal);
						}
						if (!innerAnchor.dataset.pegasusModalId && container.dataset.pegasusModalId) {
							innerAnchor.setAttribute('data-pegasus-modal-id', container.dataset.pegasusModalId);
						}

						link = innerAnchor;
					}

					// Validate the href — skip empty, fragment-only, or javascript: links.
					// We intentionally do NOT append ?modal=yes to the actual href so that
					// right-click → "Open in new tab" loads the full page.
					// The parameter is added at fetch-time in loadModalContent() instead.
					const rawHref = link.getAttribute('href') || '';

					if (rawHref === '' || rawHref.startsWith('#') || rawHref.toLowerCase().startsWith('javascript:')) {
						return;
					}

					try {
						new URL(rawHref, window.location.href);
					} catch (e) {
						return;
					}

					const existingLink = _this.doesLinkAlreadyExist(link, modalLinks);

					//check for existing link and copy id if true
					if (existingLink) {
						link.setAttribute('data-pegasus-modal-id', existingLink.dataset.pegasusModalId);
					} else {
						link.setAttribute('data-pegasus-modal-id', `modal-${index}`);
					}

					const modalClassNames = link.dataset?.pegasusModal || '';
					const shouldLoadOnClick = links.length > 4 || modalClassNames.includes('Modal--video');

					// For larger sets we lazy-load modal content on click.
					// Video modals also load on click to avoid prefetching media files.
					if (shouldLoadOnClick) {
						link.addEventListener('click', (e) => {
							e.preventDefault();
							_this.loadModalContent(link.href, link.dataset.pegasusModalId);
						});
					}

					//add event to emit 'modalopen' event for specified modal
					link.addEventListener('click', _this.emitModalOpen);

					//check we're not adding existing links.
					if (!existingLink) modalLinks.push(link);
				});
			}

			return modalLinks;
		},

		emitModalOpen(e) {
			e.preventDefault();

			const modalID = e.currentTarget.dataset.pegasusModalId;
			const myEvent = new CustomEvent('modalopen', {
				detail: { modalID },
			});

			window.dispatchEvent(myEvent);
		},

		buildModalNav(currentIndex, allLinksArray) {
			const _this = this;

			let prevLinkHTML, nextLinkHTML;

			//check if we're at the start or end
			let prevLink = currentIndex > 0 ? allLinksArray[currentIndex - 1] : '';
			let nextLink = currentIndex < allLinksArray.length - 1 ? allLinksArray[currentIndex + 1] : '';

			//check if links are modals with navs
			if (prevLink !== '') prevLink = prevLink.dataset.pegasusModal.includes('Modal--with-nav') ? prevLink : '';
			if (nextLink !== '') nextLink = nextLink.dataset.pegasusModal.includes('Modal--with-nav') ? nextLink : '';

			if (prevLink !== '') {
				const target = prevLink.dataset.pegasusModalId;
				const prevHref = prevLink.getAttribute('href') || '';

				prevLinkHTML = prevHref
					? `<a href="${prevHref}" class="prev-link arrow-btn" data-pegasus-modal-id="${target}"></a>`
					: `<span class="prev-link arrow-btn -disabled"></span>`;
			} else {
				prevLinkHTML = `<span class="prev-link arrow-btn -disabled"></span>`;
			}

			if (nextLink !== '') {
				const target = nextLink.dataset.pegasusModalId;
				const nextHref = nextLink.getAttribute('href') || '';

				nextLinkHTML = nextHref
					? `<a href="${nextHref}" class="next-link arrow-btn" data-pegasus-modal-id="${target}"></a>`
					: `<span class="next-link arrow-btn -disabled"></span>`;
			} else {
				nextLinkHTML = `<span class="next-link arrow-btn -disabled"></span>`;
			}

			//create modalNav DOM element
			const modalNav = pegasus.stringToHTML(`<div class="links">${prevLinkHTML} ${nextLinkHTML}</div>`);

			//set up modal open and ajax events on modal nav links
			modalNav.querySelectorAll('[data-pegasus-modal-id]').forEach((link) => {
				link.addEventListener('click', _this.emitModalOpen);

				link.addEventListener('click', (e) => {
					e.preventDefault();
					_this.loadModalContent(link.href, link.dataset.pegasusModalId);
				});
			});

			return modalNav;
		},

		isDirectVideoUrl(pathname = '') {
			return /\.(mp4|webm|ogg)$/i.test(pathname);
		},

		renderVideoModal(modalID, sourceUrl) {
			const modalBody = document.querySelector(`#${modalID} .Modal__body`);
			if (!modalBody) return;

			const safeSourceUrl = sourceUrl.replace(/"/g, '&quot;');

			modalBody.innerHTML = `
				<div class="Modal__media">
					<div class="Modal__video-frame">
						<video class="Modal__video" playsinline preload="metadata">
						<source src="${safeSourceUrl}">
							Your browser does not support the video tag.
						</video>
						<button type="button" class="Modal__video-play-btn" aria-label="Watch the video">
							<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M18.75 10.0004C18.7505 10.2126 18.6961 10.4213 18.5921 10.6063C18.488 10.7912 18.3379 10.9461 18.1562 11.0559L6.9 17.9418C6.71022 18.058 6.49287 18.1214 6.27037 18.1256C6.04788 18.1297 5.82832 18.0743 5.63438 17.9652C5.44227 17.8578 5.28225 17.7012 5.17075 17.5114C5.05926 17.3217 5.00032 17.1056 5 16.8855V3.11523C5.00032 2.89514 5.05926 2.67911 5.17075 2.48935C5.28225 2.29959 5.44227 2.14295 5.63438 2.03555C5.82832 1.92644 6.04788 1.87109 6.27037 1.87521C6.49287 1.87933 6.71022 1.94278 6.9 2.05898L18.1562 8.94492C18.3379 9.05467 18.488 9.20955 18.5921 9.3945C18.6961 9.57945 18.7505 9.78818 18.75 10.0004Z" fill="white"/>
							</svg>
							<span class="Modal__video-play-label">Watch the video</span>
						</button>
					</div>
				</div>
			`;

			const video = modalBody.querySelector('.Modal__video');
			const playButton = modalBody.querySelector('.Modal__video-play-btn');

			if (!video || !playButton) return;

			video.controls = false;

			playButton.addEventListener('click', (e) => {
				e.preventDefault();
				video.play().catch(() => {});
			});

			video.addEventListener('play', () => {
				video.controls = true;
				playButton.classList.add('-hidden');
			});

			video.addEventListener('ended', () => {
				video.controls = false;
				video.currentTime = 0;
				playButton.classList.remove('-hidden');
			});
		},

		loadModalContent(href, modalID) {
			let url;
			try {
				url = new URL(href, window.location.href);
			} catch (e) {
				return;
			}

			if (this.isDirectVideoUrl(url.pathname)) {
				this.renderVideoModal(modalID, url.toString());
				return;
			}

			// Append ?modal=yes at fetch-time only — the actual <a href> stays
			// clean so right-click → "Open in new tab" loads the full page. OD
			url.searchParams.set('modal', 'yes');

			let lastModalBody = null;

			pegasus
				.fetchContent(url)
				.then((resp) => {
					if (!resp.ok) throw Error(resp.statusText);

					return resp.text();
				})
				.then(async (htmlResponse) => {
					const modalHTML = htmlResponse;

					await this.preloadModalComponents(modalHTML);

					const modalBody = document.querySelector(`#${modalID} .Modal__body`);
					const modalElement = document.getElementById(modalID);

					modalBody.innerHTML = modalHTML;

					if (modalElement) {
						const hasEbookTemplate = !!modalBody.querySelector('.ebook-template');
						modalElement.classList.toggle('Modal--ebook', hasEbookTemplate);
					}

					lastModalBody = modalBody;

					return modalBody;
				})
				.then((modalBody) => {
					//reinit anything else here...
					pegasus.decodeAllCloudflareEmails(modalBody);
				})
				.catch((error) => {
					const modalBody = document.querySelector(`#${modalID} .Modal__body`),
						errorContainer = modalBody.querySelector('.Modal__error');

					if (typeof errorContainer === 'undefined' || errorContainer === null) return;

					errorContainer.classList.remove('hidden');
				})
				.finally(() => {
					if (document.lazyLoadInstance) {
						document.lazyLoadInstance.update();
					}

					// Detect which content handlers are needed and queue
					// their dynamic imports. The actual handler modules are
					// only fetched by the browser when content matches —
					// keeping the main bundle lean.
					if (lastModalBody) {
						const state = (this._modalState[modalID] = this._modalState[modalID] || {
							modalBody: null,
							handlerImports: [],
							opened: false,
						});
						state.modalBody = lastModalBody;

						state.handlerImports = this._detectHandlers(lastModalBody);

						if (state.opened) {
							this._runModalOpenHandlers(modalID);
						}
					}
				});
		},

		preloadModalComponents(modalHTML) {
			const modalMarkup = String(modalHTML || '');
			const imports = [];

			if (/GatedContentCard|x-data\s*=\s*"GatedContentCard(?:\(\))?"/i.test(modalMarkup)) {
				imports.push(import('../../blocks/GatedContentCard/script.js'));
			}

			if (!imports.length) return Promise.resolve();
			return Promise.all(imports).then(() => undefined);
		},

		/**
		 * Lightweight detection — checks the DOM for known form
		 * patterns and returns an array of dynamic import() thunks
		 * for only the handlers that are needed.
		 *
		 * No handler code is loaded until one of these thunks is called.
		 */
		_detectHandlers(modalBody) {
			const imports = [];

			// HubSpot: hs-form-frame embeds OR legacy inline scripts
			const hasHsFrame = modalBody.querySelector('.hs-form-frame[data-form-id]');
			const hasHsScript = (() => {
				for (const s of modalBody.querySelectorAll('script')) {
					if (s.src && s.src.includes('hsforms')) return true;
					if ((s.textContent || '').includes('hbspt.forms.create')) return true;
				}
				return false;
			})();

			if (hasHsFrame || hasHsScript) {
				imports.push(() => import('./handlers/hubspot.js'));
			}

			// Contact Form 7: .wpcf7 wrapper + global wpcf7 object
			if (typeof wpcf7 !== 'undefined' && wpcf7 !== null && modalBody.querySelector('.wpcf7 > form')) {
				imports.push(() => import('./handlers/cf7.js'));
			}

			return imports;
		},

		/**
		 * Dynamically import and execute every detected handler for a
		 * given modal. Called once per modal when both conditions are
		 * met: content is loaded AND modal is opened.
		 */
		_runModalOpenHandlers(modalID) {
			const state = this._modalState[modalID];
			if (!state || !state.modalBody || !state.handlerImports.length) return;

			state.handlerImports.forEach((importFn) => {
				importFn()
					.then((mod) => {
						if (typeof mod.onModalOpen === 'function') {
							mod.onModalOpen(state.modalBody, modalID);
						}
					})
					.catch(() => {});
			});

			// Clean up so handlers don't fire twice on the same modal.
			state.handlerImports = [];
		},
	};
};

/**
 * singleModal
 *
 * Component code for a single modal
 *
 * @since  2.0.0
 */
window.singleModal = () => {
	return {
		open: false,

		handleModalOpen(evt) {
			//only run on actual modals, not skeleton
			if (this.$el.classList.contains('ModalSkeleton')) return;

			document.body.classList.add('modal-open');
			document.body.style.overflow = 'hidden';
			this.open = this.$el.id === evt.detail.modalID;
		},

		handleModalClose() {
			this.$el.querySelectorAll('video').forEach((video) => {
				if (typeof video.pause === 'function') {
					video.pause();
				}
			});

			document.body.classList.remove('modal-open');
			document.body.style.overflow = '';
			this.open = false;
		},
	};
};
